import { Grid } from '@mui/material';
import { CustomFieldDefinition, CustomFieldDefinitionModels } from '@nx-smartmonkey/shared/domain';
import {
  HSpacer,
  RoutalDialog,
  RoutalPalette,
  RoutalSelect,
  RoutalSelectMultiSelectOption,
  VSpacer,
} from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import More3Points from '../../../assets/svg/More3Points';
import PlusIcon from '../../../assets/svg/settings/PlusIcon';
import { HeaderContent } from '../../../components/HeaderContent';
import { TableColumnsSortConfig, sortDataByTableColumns } from '../../../context/shared/tableColumns';
import { intl } from '../../../helpers/IntGlobalProvider';
import { useCreateProjectCustomField } from '../../../hooks/project/useCreateProjectCustomField';
import { useDeleteProjectCustomField } from '../../../hooks/project/useDeleteProjectCustomField';
import { useRetrieveActiveProject } from '../../../hooks/project/useRetrieveActiveProject';
import { useUpdateProjectCustomField } from '../../../hooks/project/useUpdateProjectCustomField';
import { useRetrieveUser } from '../../../hooks/user/useRetrieveUser';
import { useRetrieveUserCredentials } from '../../../hooks/user/useRetrieveUserCredentials';
import Button from '../../../react-components/Button';
import InputText from '../../../react-components/InputText';
import Text from '../../../react-components/Text';
import { DataTable } from '../../../react-components/data-table/DataTable';
import { ModelToIntlId } from './IntlMapper';
import { ProjectCustomFieldCreateDialog } from './ProjectCustomFieldCreateDialog';
import { ProjectCustomFieldCategoryIcon } from './ProjectCustomFieldIcons';
import { ProjectCustomFieldUpdateDialog } from './ProjectCustomFieldUpdateDialog';
import { getCustomFieldsColumns, tableColumns } from './ProjectCustomFieldsColumns';

type ProjectCustomFieldsProps = `name` | `category` | `field_type` | `updated_at` | `custom_id`;

export const ProjectCustomFields = () => {
  // const { retrieveDoc } = useRetrieveSupportDocs();
  const { data: user } = useRetrieveUser();
  const { data: project } = useRetrieveActiveProject();
  const { data: credentials } = useRetrieveUserCredentials();
  const [selectedCategory, setSelectedCategory] = useState<CustomFieldDefinitionModels | `all`>(`all`);
  const [sortPlansInfo, setSortPlansInfo] = useState<TableColumnsSortConfig<ProjectCustomFieldsProps>>({
    orderBy: `name`,
    order: `desc`,
  });

  const { run: createCustomField } = useCreateProjectCustomField();
  const { run: updateCustomField } = useUpdateProjectCustomField();
  const { run: removeCustomField } = useDeleteProjectCustomField();

  const [createCustomFieldDialog, setCreateCustomFieldDialog] = useState<boolean>(false);

  const [currentField, setCurrentField] = useState<CustomFieldDefinition | undefined>(undefined);

  const [confirmDeleteCustomFieldId, setConfirmDeleteCustomFieldId] = useState<string>(``);
  const [customFields, setCustomFields] = useState<CustomFieldDefinition[]>([]);

  const [fieldToRemove, setFieldToRemove] = useState<CustomFieldDefinition | undefined>(undefined);

  const [confirmDeleteCustomFieldDialog, setConfirmDeleteCustomFieldDialog] = useState<boolean>(false);

  const [options, setOptions] = useState<CustomFieldDefinitionModels[]>([]);

  useEffect(() => {
    if (!project || !credentials) return;

    setCustomFields(project.custom_fields?.getAll() ?? []);

    const tasksEnabled = credentials.subscription.test(`planner.tasks`) && project.tasks_enabled;
    const reportsEnabled = credentials.subscription.test(`drivers.advanced_pod`);

    const newOptions = (
      [
        `client`,
        `vehicle`,
        `task`,
        `service_report_completed`,
        `service_report_canceled`,
        `service_report_task_completed`,
        `service_report_task_canceled`,
      ] as CustomFieldDefinitionModels[]
    ).filter((option) => {
      if (!tasksEnabled && option.includes(`task`)) return false;
      if (!reportsEnabled && option.includes(`report`)) return false;
      return true;
    });
    setOptions(newOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  if (!project || !user || !credentials) {
    return null;
  }

  const filterByCredentials = (customFields: CustomFieldDefinition[]): CustomFieldDefinition[] => {
    const tasksEnabled = credentials.subscription.test(`planner.tasks`) && project.tasks_enabled;
    const reportsEnabled = credentials.subscription.test(`drivers.advanced_pod`);

    const filteredCustomFields = customFields.filter((customField) => {
      if (
        !tasksEnabled &&
        (
          [`task`, `service_report_task_canceled`, `service_report_task_completed`] as CustomFieldDefinitionModels[]
        ).includes(customField.field_model)
      ) {
        return false;
      }
      if (
        !reportsEnabled &&
        ([`service_report_completed`, `service_report_canceled`] as CustomFieldDefinitionModels[]).includes(
          customField.field_model
        )
      ) {
        return false;
      }
      return true;
    });
    return filteredCustomFields;
  };

  const filterByCategory = (customFields: CustomFieldDefinition[]): CustomFieldDefinition[] => {
    if (selectedCategory === `all`) {
      return customFields;
    }

    return customFields.filter((customField) => customField.field_model === selectedCategory);
  };

  const filterCustomFields = (customFields: CustomFieldDefinition[]): CustomFieldDefinition[] => {
    return filterByCategory(filterByCredentials(customFields));
  };

  const finalCustomFields = sortDataByTableColumns({
    data: filterCustomFields(customFields),
    tableColumns,
    sortInfo: sortPlansInfo,
  });

  return (
    <Grid>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        style={{ padding: `32px 44px 16px 44px`, borderBottom: `1px solid ${RoutalPalette.neutral10}` }}
      >
        <Grid>
          <HeaderContent
            titleIntlId="projects.settings.fields.custom_fields"
            subtitle="projects.settings.fields.custom_fields.subtitle"
            // article={{
            //   intlId: `projects.settings.fields.custom_fields.article`,
            //   link: retrieveDoc({ supportPage: `createCustomFields`, language: user?.language.value }),
            // }}
          />
        </Grid>
        <Grid>
          <Button
            style={{ display: `flex`, flexDirection: `row`, alignItems: `center` }}
            onClick={() => setCreateCustomFieldDialog(true)}
          >
            <PlusIcon />
            <Text
              intlId="projects.settings.fields.custom_fields.create_button"
              style={{ color: RoutalPalette.neutral00, paddingLeft: `4px` }}
            />
          </Button>
        </Grid>
      </Grid>

      <VSpacer medium />

      <Grid container direction="row" style={{ padding: `0px 44px`, width: `auto`, justifyContent: `flex-start` }}>
        <RoutalSelect
          id="category-filter"
          containerStyle={{
            maxWidth: `200px`,
          }}
          popoverProps={{
            options: ([`all`].concat(options) as (CustomFieldDefinitionModels | `all`)[]).map(
              (category, index): RoutalSelectMultiSelectOption => ({
                key: category,
                onClick: () => {
                  setSelectedCategory(category);
                },
                content: (
                  <Grid container direction="row" wrap="nowrap" alignItems="center" style={{ overflow: `hidden` }}>
                    {category !== `all` && (
                      <>
                        <ProjectCustomFieldCategoryIcon fieldModel={category} />
                        <HSpacer small />
                      </>
                    )}
                    <Text
                      tagName="span"
                      style={{
                        textAlign: `justify`,
                        whiteSpace: `nowrap`,
                        overflow: `hidden`,
                        width: `100%`,
                        textOverflow: `ellipsis`,
                        cursor: `pointer`,
                      }}
                      intlId={ModelToIntlId[category]}
                    />
                  </Grid>
                ),
              })
            ),
          }}
        >
          <Grid container direction="row" wrap="nowrap" alignItems="center" style={{ overflow: `hidden` }}>
            {selectedCategory !== `all` && (
              <>
                <ProjectCustomFieldCategoryIcon fieldModel={selectedCategory} />
                <HSpacer small />
              </>
            )}
            <Text
              style={{
                cursor: `pointer`,
                width: `100%`,
                textAlign: `justify`,
              }}
              intlId={ModelToIntlId[selectedCategory]}
            />
          </Grid>
        </RoutalSelect>
      </Grid>

      <VSpacer medium />

      <Grid
        container
        direction="column"
        style={{
          padding: `0px 44px`,
        }}
      >
        <DataTable
          noFooter
          docs={finalCustomFields.map((customField: CustomFieldDefinition) => ({ doc: customField }))}
          total={finalCustomFields.length ?? 0}
          showCheckboxes={false}
          onColumnSort={(column: string, order: `asc` | `desc`) => {
            setSortPlansInfo({ order, orderBy: column as ProjectCustomFieldsProps });
          }}
          emptyMessage={<Text intlId="projects.settings.fields.custom_fields.empty" />}
          sortBy={sortPlansInfo.orderBy}
          sortDirection={sortPlansInfo.order}
          columns={[
            ...getCustomFieldsColumns(),
            {
              accessorName: `id`,
              align: `right`,
              width: 40,
              flex: 1,
              formatter: ({ doc, rowHover }: { doc: CustomFieldDefinition; rowHover: boolean }) => (
                <Grid container justifyContent="flex-end" style={{ minHeight: `28px` }}>
                  {rowHover && (
                    <RoutalSelect
                      id={`custom_field-${doc.custom_id}-actions`}
                      hideArrow
                      style={{
                        width: `28px`,
                        height: `28px`,
                        padding: `2px`,
                        margin: `0px`,
                        border: `none`,
                        backgroundColor: `transparent`,
                      }}
                      popoverProps={{
                        options: [
                          {
                            key: `edit`,
                            onClick: () => {
                              setCurrentField(doc);
                            },
                            content: <Text intlId="edit" style={{ cursor: `pointer` }} />,
                          },
                          // WARNING!! -> THIS IS TEMPORAL UNTIL WE HAVE A WAY TO ENABLE AND DISABLE REPORT CUSTOM FIELDS IN PLANNER. (start)
                          ...([
                            `service_report_completed`,
                            `service_report_canceled`,
                            `service_report_task_completed`,
                            `service_report_task_canceled`,
                          ].includes(doc.field_model)
                            ? [
                                {
                                  key: doc.enabled ? `disable_for_planner` : `enable_for_planner`,
                                  onClick: () => {
                                    updateCustomField({
                                      enabled: !doc.enabled,
                                      customField: doc,
                                    });
                                  },
                                  content: (
                                    <Text
                                      intlId={
                                        doc.enabled
                                          ? `projects.settings.fields.custom_fields.disable_for_planner`
                                          : `projects.settings.fields.custom_fields.enable_for_planner`
                                      }
                                      style={{ cursor: `pointer` }}
                                    />
                                  ),
                                },
                              ]
                            : []),
                          // WARNING!! -> THIS IS TEMPORAL UNTIL WE HAVE A WAY TO ENABLE AND DISABLE REPORT CUSTOM FIELDS IN PLANNER. (end)
                          null,
                          {
                            key: `delete`,
                            onClick: () => {
                              setConfirmDeleteCustomFieldId(``);
                              setFieldToRemove(doc);
                              setConfirmDeleteCustomFieldDialog(true);
                            },
                            content: (
                              <Text intlId="delete" style={{ color: RoutalPalette.error.medium, cursor: `pointer` }} />
                            ),
                          },
                        ],
                      }}
                    >
                      <More3Points />
                    </RoutalSelect>
                    // <EditRemoveMenu
                    //   id={`custom_field-${doc.custom_id}-actions`}
                    //   onEdit={() => {
                    //     setCurrentField(doc);
                    //   }}
                    //   onDelete={() => {
                    //     setConfirmDeleteCustomFieldId(``);
                    //     setFieldToRemove(doc);
                    //     setConfirmDeleteCustomFieldDialog(true);
                    //   }}
                    // />
                  )}
                </Grid>
              ),
            },
          ]}
        />
      </Grid>

      {createCustomFieldDialog && (
        <ProjectCustomFieldCreateDialog
          open={createCustomFieldDialog}
          options={options}
          project={project}
          onCloseDialog={() => setCreateCustomFieldDialog(false)}
          handleCreateCustomField={(customFieldDefinition) => {
            setCreateCustomFieldDialog(false);
            createCustomField({ customFieldDefinition });
          }}
        />
      )}

      {currentField && (
        <ProjectCustomFieldUpdateDialog
          open={currentField !== undefined}
          currentField={currentField}
          onCloseDialog={() => setCurrentField(undefined)}
          handleUpdateCustomField={(customFieldDefinition: CustomFieldDefinition) => {
            updateCustomField({
              label: customFieldDefinition.label,
              description: customFieldDefinition.description,
              customField: customFieldDefinition,
            });
            setCurrentField(undefined);
          }}
        />
      )}

      {fieldToRemove ? (
        <RoutalDialog
          open={!!confirmDeleteCustomFieldDialog}
          onCloseDialog={() => {
            setConfirmDeleteCustomFieldDialog(false);
            setFieldToRemove(undefined);
          }}
          dialogProps={{
            disableEscapeKeyDown: true,
          }}
          titleContent={<Text variant="h6" intlId="projects.settings.fields.custom_fields.delete_dialog.title" />}
          footerContent={
            <Grid container justifyContent="flex-end">
              <Button
                onClick={() => {
                  setConfirmDeleteCustomFieldDialog(false);
                  setFieldToRemove(undefined);
                }}
                variant="flat"
                intlId="settings.buttoncancel"
              />

              <HSpacer small />

              <Button
                disabled={confirmDeleteCustomFieldId !== fieldToRemove.custom_id}
                style={{
                  backgroundColor:
                    confirmDeleteCustomFieldId !== fieldToRemove.custom_id
                      ? `rgba(0, 0, 0, 0.12)`
                      : RoutalPalette.error.medium,
                }}
                onClick={() => {
                  if (fieldToRemove) {
                    removeCustomField({ customField: fieldToRemove });
                  }
                  setConfirmDeleteCustomFieldId(``);
                  setConfirmDeleteCustomFieldDialog(false);
                }}
              >
                <Text
                  style={{
                    color: RoutalPalette.white,
                  }}
                  intlId="projects.settings.fields.custom_fields.delete_dialog.title"
                />
              </Button>
            </Grid>
          }
        >
          <Text>
            <Text
              intlId="projects.settings.fields.custom_fields.delete_dialog.info"
              intlValues={{ id: fieldToRemove.custom_id }}
            />
          </Text>

          <VSpacer small />

          <VSpacer medium />

          <Text isBold intlId="projects.settings.fields.custom_fields.delete_dialog.label" />

          <VSpacer small />

          <InputText
            id="delete_custom_field_confirmation_input"
            placeholder={intl.formatMessage({
              id: `projects.settings.fields.custom_fields.delete_dialog.label_ph`,
            })}
            fieldValue={confirmDeleteCustomFieldId}
            onChange={(value: any) => setConfirmDeleteCustomFieldId(value)}
          />
        </RoutalDialog>
      ) : null}
    </Grid>
  );
};
